import { createStore } from '@shared/utils/state/state-factory'
import { usePhoneStore } from 'v2/state/phone'
import { AgentChannels, type StoreActions } from 'v2/types'
import { agentLogger } from 'v2/utils/logger'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { sidebarComponentMap } from './config/sidebar.config'
import { useFlowRunStore } from './run-info/store'
import {
  EditorType,
  type FlowActions,
  FlowNodeTypes,
  type FlowState,
  SYSTEM_PROMPT_NODE_ID,
} from './types'
import { checkUnsavedChanges } from './utils'

const initialState: FlowState = {
  selectedNodeID: null, // id of the selected node
  unsavedModalOpen: false,
  flowInstance: null, // the instance of the flow, use it rarely
  sidebarFormInstance: null, // the instance of the form in the sidebar, use it rarely
  isEditing: false, // whether the flow is in edit mode
  showVibeSidebar: false,
  vibeSidebarWidth: 550,
  showEvalSidebar: false,
  agentSettingsActiveTab: 'knowledge-base', // active tab in agent settings sidebar
  agentSettings: null,
  initialAgentSettings: null,
  tabFormInstances: {},
  candidateNode: null,
  isSelectionModeActive: false, // whether selection mode is active
  showTriggerDialog: false,
  showTriggerChangeConfirmationModal: false,
  triggerChangeData: null,
  callTriggerPrompt: null,
  showNodeSelectionModal: false,
  currentFlowType: null,
  // initial default channel is call
  agentMeta: {
    defaultChannel: AgentChannels.call,
  },
  glowingNodeID: null,
  isValidationErrorPopoverOpen: false,
  systemPrompt: null,
  isGlobalMetaLoaded: false,
  sttGuidance: null,
  agentName: null,
  agentPersona: null,
  currentFlowName: null,
  flowNameUserEdited: false,
  shouldStreamFlowNameEffect: false,
  lastEditorType: null,
  vibeAgentContributed: false,
  hasSettingsValidationError: false,
  requestedPath: null,
  nameFocusNonce: 0,
  inputsSeededNodeIds: new Set(),
  inputsProposedNodeIds: new Set(),
  collectionDetection: { running: null, errors: {}, failure: null, notice: null },
}

const actions: StoreActions<FlowState, FlowActions> = (set, get) => ({
  selectNode: (nodeID) => {
    const node = get().flowInstance?.getNode(nodeID)
    // Allow note nodes to be selected even if they don't have a sidebar component
    const shouldSelect =
      nodeID === SYSTEM_PROMPT_NODE_ID ||
      (node?.type &&
        (sidebarComponentMap[node.type as FlowNodeTypes] || node.type === FlowNodeTypes.note))
    set({ selectedNodeID: shouldSelect ? nodeID : null })
  },
  setCandidateNode: (node) => set({ candidateNode: node }),
  setFlowInstance: (instance) => set({ flowInstance: instance }),
  setAgentSettings: (settings) =>
    set((state) => ({ agentSettings: { ...state.agentSettings, ...settings } })),
  setInitialAgentSettings: (settings) => set({ initialAgentSettings: settings }),
  setTabFormInstance: (key, form) =>
    set((state) => ({ tabFormInstances: { ...state.tabFormInstances, [key]: form } })),
  setSidebarFormInstance: (instance) => set({ sidebarFormInstance: instance }),
  requestNameFieldFocus: () => set((state) => ({ nameFocusNonce: state.nameFocusNonce + 1 })),
  setIsEditing: (isEditing, nodeId) =>
    set({
      isEditing,
      selectedNodeID: nodeId,
    }),
  setAgentSettingsActiveTab: (tab) => set({ agentSettingsActiveTab: tab }),
  setShowVibeSidebar: (show) =>
    set((state) => ({
      showVibeSidebar: show,
      showEvalSidebar: show ? false : state.showEvalSidebar,
    })),
  setVibeSidebarWidth: (width) => set({ vibeSidebarWidth: width }),
  setShowEvalSidebar: (show) =>
    set((state) => ({
      showEvalSidebar: show,
      showVibeSidebar: show ? false : state.showVibeSidebar,
      selectedNodeID: show ? null : state.selectedNodeID,
    })),
  // Unselect the node if the form data is the same as the node data
  // Otherwise, open the unsaved modal
  unselectNode: (save = false) => {
    if (save) {
      return set({
        selectedNodeID: null,
        unsavedModalOpen: false,
      })
    }

    const unsavedModalOpen = get().isEditing ? checkUnsavedChanges() : false

    set((state) => ({
      selectedNodeID: unsavedModalOpen ? state.selectedNodeID : null,
      unsavedModalOpen,
      showTriggerDialog: false,
    }))
  },
  discardChanges: () => set({ selectedNodeID: null, unsavedModalOpen: false }),
  reset: () => {
    set(initialState)
    useFlowRunStore.getState().reset()
    const { activeCall } = usePhoneStore.getState()
    if (activeCall?.contactName === 'Playground') phoneSDK.hangup()
  },
  setUnsavedModalOpen: (open) => set({ unsavedModalOpen: open }),
  toggleSelectionMode: () =>
    set((state) => ({ isSelectionModeActive: !state.isSelectionModeActive })),
  setShowTriggerDialog: (show) => set({ showTriggerDialog: show }),
  setShowTriggerChangeConfirmationModal: (show, data) =>
    set({
      showTriggerChangeConfirmationModal: !!show && !!data, // Only open when we actually have the payload
      triggerChangeData: show && data ? data : null,
    }),
  setCallTriggerPrompt: (data) => set({ callTriggerPrompt: data }),
  setCurrentFlowType: (type) => set({ currentFlowType: type }),
  setAgentMeta: (meta) => set({ agentMeta: { ...get().agentMeta, ...meta } }),
  setGlowingNodeID: (id) => set({ glowingNodeID: id }),
  toggleValidationErrorPopover: () =>
    set((state) => ({ isValidationErrorPopoverOpen: !state.isValidationErrorPopoverOpen })),
  setSystemPrompt: (systemPrompt, selectNode = true) =>
    set({
      systemPrompt,
      selectedNodeID: systemPrompt && selectNode ? SYSTEM_PROMPT_NODE_ID : null,
    }),
  setIsGlobalMetaLoaded: (isLoaded) => set({ isGlobalMetaLoaded: isLoaded }),
  setSttGuidance: (sttGuidance) => set({ sttGuidance }),
  setAgentName: (agentName) => set({ agentName }),
  setAgentPersona: (agentPersona) => set({ agentPersona }),
  setCurrentFlowName: (name) => set({ currentFlowName: name }),
  setFlowNameUserEdited: (flowNameUserEdited) => set({ flowNameUserEdited }),
  // get() + a new Set, like setAgentMeta: reset() restores initialState so its Set must stay
  // empty, and an object-style set skips immer, which cannot draft a Set without enableMapSet.
  markInputsSeeded: (nodeId) =>
    set({ inputsSeededNodeIds: new Set(get().inputsSeededNodeIds).add(nodeId) }),
  markInputsProposed: (nodeId) =>
    set({ inputsProposedNodeIds: new Set(get().inputsProposedNodeIds).add(nodeId) }),
  setCollectionDetection: (patch) =>
    set({ collectionDetection: { ...get().collectionDetection, ...patch } }),
  // A pass's result is about one node: it lands only while that node is on screen and no
  // other node's pass has taken the flag over.
  writeDetection: (nodeId, patch) => {
    const { selectedNodeID, collectionDetection } = get()
    const owns = collectionDetection.running === nodeId || collectionDetection.running === null
    if (selectedNodeID !== nodeId || !owns) {
      agentLogger.debug(`collection detection result dropped for ${nodeId}`, patch)
      return
    }
    set({ collectionDetection: { ...collectionDetection, ...patch } })
  },
  setShouldStreamFlowNameEffect: (shouldStream) =>
    set({ shouldStreamFlowNameEffect: shouldStream }),
  setShowNodeSelectionModal: (show) => set({ showNodeSelectionModal: show }),
  markVibeAgentChange: () =>
    set({ lastEditorType: EditorType.VIBE_AGENT, vibeAgentContributed: true }),
  markUserChange: () => set({ lastEditorType: EditorType.USER }),
  markVibeAgentChangeForNodes: (nodeIds: string[]) => {
    const flowInstance = get().flowInstance
    if (flowInstance) {
      nodeIds.forEach((nodeId) => {
        flowInstance.updateNodeData(nodeId, (node) => ({
          meta: { ...node.data.meta, last_editor_type: EditorType.VIBE_AGENT },
        }))
      })
    }
    set({ lastEditorType: EditorType.VIBE_AGENT, vibeAgentContributed: true })
  },
  resetEditorTracking: () => set({ lastEditorType: null, vibeAgentContributed: false }),
  getEditorMetadata: () => ({
    last_editor_type: get().lastEditorType ?? EditorType.USER,
    vibe_agent_contributed: get().vibeAgentContributed,
  }),
  setHasSettingsValidationError: (hasError) => set({ hasSettingsValidationError: hasError }),
  setRequestedPath: (path) => set({ requestedPath: path }),
})

export const useFlowStore = createStore<FlowState & FlowActions>('flow', (set, get) => ({
  // State
  ...initialState,
  ...actions(set, get),
}))

// Re-export the flow run store for convenience
export { useFlowRunStore }
